import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronRight, FiChevronDown, FiLayers, FiList, FiFileText, FiPlus, FiX } from 'react-icons/fi';
import { crosswalkApi } from '../../../services/api';
import SourceDataSelector from './SourceDataSelector';

// Saved source ("scope") profiles for the assessment(s) being shared. A profile is a stored
// pick of source assessments + evidence documents that the crosswalk may draw from, and it
// can cover several target assessments — the endpoint is queried once per target and the
// deduplicated results are grouped back under every target they name.
//
//   Target assessment
//     └ Profile          (multi-select: any number of profiles may be picked)
//         ├ Source assessments
//         └ Documents
//
// "New profile" swaps the list for the create form, where SourceDataSelector supplies the raw
// tree of assessments and documents to pick from. Outside that form the tree stays hidden —
// picking a saved profile is the normal path.
//
// The selection is published upward as the chosen profiles plus the union of the assessment
// ids and document keys they carry.

const KEY_SEP = '::';

// The API returns the three collections a profile carries as `{ id: label }` maps —
// `target_assessments`, `source_assessments` and `document_selection.source_documents`.
// Flatten each into `[{ key, label }]` so the rows can show names instead of bare ids.
const entryList = (map) => {
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map).map(([key, label]) => ({
    key: String(key),
    label: (typeof label === 'string' && label.trim()) || String(key),
  }));
};

// Names shown on the expanded rows; falls back to the id when the API sends a blank label.
const labelsOf = (entries) => entries.map(e => e.label);

const normalizeProfile = (p) => {
  const targets = entryList(p.target_assessments);
  const sources = entryList(p.source_assessments);
  const documents = entryList(p.document_selection?.source_documents);
  return {
    id: p.source_scope_id,
    name: (p.name || 'Untitled profile').trim(),
    // A profile can cover several targets, so it may appear under more than one group.
    targetAssessmentIds: targets.map(t => t.key),
    targetAssessments: targets,
    sourceAssessments: sources,
    sourceAssessmentIds: sources.map(s => s.key),
    documents,
    documentKeys: documents.map(d => d.key),
    documentMode: p.document_selection?.mode || null,
    includeUnboundDocuments:
      p.include_unbound_documents ?? p.document_selection?.include_unbound_documents ?? false,
    updatedAt: p.updated_at || p.created_at || null,
  };
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const CheckMark = ({ checked }) => (
  <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
    checked ? 'bg-[#0077b5] border-[#0077b5]' : 'border-[#919191]/50 bg-white'
  }`}>
    {checked && <FiCheck className="text-white text-xs" />}
  </div>
);

const Row = ({ depth = 0, selectable, checked, onToggle, expandable, expanded, onToggleExpand, icon, children, meta }) => (
  <div
    onClick={onToggle}
    style={{ paddingLeft: `${12 + depth * 16}px` }}
    className={`flex items-center gap-2 pr-3 py-2.5 border-b last:border-b-0 transition-colors ${
      onToggle ? 'cursor-pointer' : ''
    } ${
      checked
        ? 'bg-[#0077b5]/10 border-[#0077b5]/20 text-[#53545a]'
        : 'border-[#919191]/20 text-[#919191] hover:bg-slate-50'
    }`}
  >
    {expandable ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
        className="p-0.5 -ml-1 text-[#919191] hover:text-[#53545a] flex-shrink-0"
        aria-label={expanded ? 'Collapse' : 'Expand'}
      >
        {expanded ? <FiChevronDown className="text-sm" /> : <FiChevronRight className="text-sm" />}
      </button>
    ) : (
      <span className="w-[18px] flex-shrink-0" />
    )}
    {selectable ? <CheckMark checked={checked} /> : <span className="w-4 flex-shrink-0" />}
    {icon}
    <div className="flex-1 min-w-0">{children}</div>
    {meta && <span className="text-xs text-[#919191] flex-shrink-0">{meta}</span>}
  </div>
);

const SourceProfileSelector = ({ isOpen, targetAssessmentIds, assessments = [], onSelectionChange }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const cache = useRef({});

  // Chosen profiles, as a set of source_scope_ids. Any number can be picked; a profile that
  // covers several targets is one selection no matter which group it was ticked in.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  // Selection to apply once the next `profiles` list lands (set right after creating one).
  const pendingSelection = useRef(null);

  // Create form
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftSelection, setDraftSelection] = useState({ assessmentIds: [], documentKeys: [] });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const targetKey = useMemo(
    () => (targetAssessmentIds && targetAssessmentIds.length > 0)
      ? targetAssessmentIds.slice().sort().join(',')
      : null,
    [targetAssessmentIds]
  );

  const toggleExpanded = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Load the saved profiles — one request per target assessment. `force` skips the cache,
  // which is how the list refreshes after a profile is created.
  const loadProfiles = useCallback((force = false) => {
    if (!isOpen || !targetKey) {
      setProfiles([]);
      setError(false);
      return undefined;
    }
    if (!force && cache.current[targetKey]) {
      setProfiles(cache.current[targetKey]);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    crosswalkApi.getSourceProfilesForTargets(targetAssessmentIds)
      .then(data => {
        const resolved = data.filter(p => p?.source_scope_id).map(normalizeProfile);
        cache.current[targetKey] = resolved;
        if (!cancelled) setProfiles(resolved);
      })
      .catch(err => {
        console.error('Failed to load source profiles for assessments', targetAssessmentIds, err);
        if (!cancelled) {
          setProfiles([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, targetKey, targetAssessmentIds]);

  useEffect(() => loadProfiles(), [loadProfiles]);

  // Nothing is preselected — a profile narrows the crosswalk, so the user opts in. The one
  // exception is a profile that was just created, parked here so it survives the refetch.
  useEffect(() => {
    setSelectedIds(new Set(pendingSelection.current || []));
    pendingSelection.current = null;
    setExpanded(new Set());
  }, [profiles]);

  // Group the flat list back under the targets each profile covers, keeping the order of the
  // targets the caller gave us. A profile listing several targets shows up under each of them.
  const groups = useMemo(() => {
    const byTarget = new Map();
    profiles.forEach(p => {
      p.targetAssessmentIds.forEach(targetId => {
        if (!byTarget.has(targetId)) byTarget.set(targetId, []);
        byTarget.get(targetId).push(p);
      });
    });
    return (targetAssessmentIds || [])
      .map(id => {
        const key = String(id);
        const assessment = assessments.find(a => String(a.id) === key);
        const targetProfiles = byTarget.get(key) || [];
        return {
          id: key,
          label:
            assessment?.assessmentDescription
            // The profile payload names its targets, so use that when the caller didn't.
            || targetProfiles[0]?.targetAssessments.find(t => t.key === key)?.label
            || `Assessment #${key}`,
          profiles: targetProfiles,
        };
      })
      .filter(g => g.profiles.length > 0);
  }, [profiles, targetAssessmentIds, assessments]);

  const selectedProfiles = useMemo(
    () => profiles.filter(p => selectedIds.has(p.id)),
    [profiles, selectedIds]
  );

  // A profile can apply to several targets, so resolve each target to every selected profile
  // that covers it. The crosswalk uses the first (a bulk target carries one source_scope_id);
  // the rest are still recorded on the share so the activity log shows the full picked scope.
  // A target with no matching selection runs unscoped.
  const sourceScopeMap = useMemo(() => {
    const map = {};
    (targetAssessmentIds || []).forEach(id => {
      const key = String(id);
      const matches = selectedProfiles.filter(p => p.targetAssessmentIds.includes(key));
      if (matches.length > 0) map[key] = matches.map(p => p.id);
    });
    return map;
  }, [targetAssessmentIds, selectedProfiles]);

  // Document titles live in the agentic API, not our DB, so the share carries a
  // key -> title lookup the backend uses purely to label the documents it reads itself.
  const documentTitles = useMemo(() => {
    const titles = {};
    selectedProfiles.forEach(p => {
      p.documents.forEach(d => {
        if (d.key && d.label) titles[d.key] = d.label;
      });
    });
    return titles;
  }, [selectedProfiles]);

  // Publish the selection upward: the profiles themselves plus the union of what they carry.
  useEffect(() => {
    if (!onSelectionChange) return;
    const assessmentIds = new Set();
    const documentKeys = new Set();
    selectedProfiles.forEach(p => {
      p.sourceAssessmentIds.forEach(id => assessmentIds.add(id));
      p.documentKeys.forEach(k => documentKeys.add(k));
    });
    onSelectionChange({
      profiles: selectedProfiles,
      sourceScopeIds: selectedProfiles.map(p => p.id),
      sourceScopeMap,
      documentTitles,
      assessmentIds: [...assessmentIds],
      documentKeys: [...documentKeys],
    });
  }, [selectedProfiles, sourceScopeMap, documentTitles, onSelectionChange]);

  // Multi-select: each profile toggles on its own, and a profile listed under several targets
  // is the same selection in every group it appears in.
  const toggleProfile = (profile) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(profile.id)) next.delete(profile.id);
      else next.add(profile.id);
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  const startCreating = () => {
    setDraftName('');
    setDraftSelection({ assessmentIds: [], documentKeys: [] });
    setSaveError(null);
    setCreating(true);
  };

  const cancelCreating = () => {
    setCreating(false);
    setSaveError(null);
  };

  // A profile is stored per target, so sharing several assessments saves the same picks once
  // per target and auto-selects each of the results.
  const saveProfile = async () => {
    const name = draftName.trim();
    if (!name || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const created = await Promise.all(
        (targetAssessmentIds || []).map(targetAssessmentId =>
          crosswalkApi.createSourceProfile({
            name,
            targetAssessmentId,
            sourceAssessmentIds: draftSelection.assessmentIds,
            documentKeys: draftSelection.documentKeys,
          })
        )
      );
      // Preselect what was just saved — parked in the ref so the refetch's reset applies it
      // instead of clearing it.
      pendingSelection.current = created
        .filter(p => p?.source_scope_id)
        .map(p => p.source_scope_id);
      delete cache.current[targetKey];
      loadProfiles(true);
      setCreating(false);
    } catch (err) {
      console.error('Failed to create source profile', err);
      setSaveError('Could not save the source profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasRows = groups.length > 0;
  // A single target doesn't need a header row above its own profiles.
  const flatten = groups.length === 1;
  const canCreate = !!targetKey;

  const renderProfile = (profile, depth, targetId) => {
    // The row key is scoped by target so the same profile can expand independently under each.
    const pKey = `p${KEY_SEP}${targetId}${KEY_SEP}${profile.id}`;
    const pExpanded = expanded.has(pKey);
    const checked = selectedIds.has(profile.id);
    const updated = formatDate(profile.updatedAt);
    const sourceNames = labelsOf(profile.sourceAssessments);
    const documentNames = labelsOf(profile.documents);
    // The source-assessments and documents rows are each collapsible on their own, nested one
    // level deeper than the profile row they belong to.
    const sKey = `s${KEY_SEP}${targetId}${KEY_SEP}${profile.id}`;
    const sExpanded = expanded.has(sKey);
    const dKey = `d${KEY_SEP}${targetId}${KEY_SEP}${profile.id}`;
    const dExpanded = expanded.has(dKey);
    return (
      <React.Fragment key={pKey}>
        <Row
          depth={depth}
          selectable
          checked={checked}
          onToggle={() => toggleProfile(profile)}
          expandable
          expanded={pExpanded}
          onToggleExpand={() => toggleExpanded(pKey)}
          icon={<FiLayers className={`text-base flex-shrink-0 ${
            checked ? 'text-[#F47F2E]' : 'text-[#919191]/50'
          }`} />}
          meta={`${sourceNames.length} assessments • ${documentNames.length} docs`}
        >
          <span className="text-sm block truncate" title={profile.name}>{profile.name}</span>
          {updated && <span className="text-[11px] block text-[#919191]">Updated {updated}</span>}
        </Row>

        {pExpanded && (
          <>
            <Row
              depth={depth + 1}
              icon={<FiList className={`text-sm flex-shrink-0 ${sExpanded ? 'text-[#0077b5]' : 'text-[#919191]/70'}`} />}
              expandable={sourceNames.length > 0}
              expanded={sExpanded}
              onToggleExpand={() => toggleExpanded(sKey)}
              meta={sourceNames.length > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                  sExpanded ? 'bg-[#0077b5] text-white' : 'bg-[#919191]/15 text-[#919191]'
                }`}>
                  {sourceNames.length}
                </span>
              )}
            >
              <span className="text-xs block">Source assessments</span>
              {sourceNames.length === 0 && (
                <span className="text-[11px] block text-[#919191]">None</span>
              )}
            </Row>
            {sExpanded && sourceNames.length > 0 && (
              <div
                className="border-b last:border-b-0 border-[#919191]/20 bg-gradient-to-b from-[#0077b5]/[0.04] to-transparent py-2.5 pr-3"
                style={{ paddingLeft: `${12 + (depth + 2) * 16}px` }}
              >
                <div className="flex flex-wrap gap-1.5">
                  {sourceNames.map((name, i) => (
                    <span
                      key={`${sKey}${KEY_SEP}${i}`}
                      title={name}
                      className="group inline-flex items-center gap-1.5 max-w-[240px] pl-2 pr-2.5 py-1 text-[11px] leading-none bg-white border border-[#0077b5]/25 text-[#53545a] rounded-full shadow-sm hover:border-[#0077b5]/50 hover:shadow transition-all"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0077b5] flex-shrink-0 group-hover:scale-125 transition-transform" />
                      <span className="truncate">{name}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Row
              depth={depth + 1}
              icon={<FiFileText className={`text-sm flex-shrink-0 ${dExpanded ? 'text-[#0077b5]' : 'text-[#919191]/70'}`} />}
              expandable={documentNames.length > 0}
              expanded={dExpanded}
              onToggleExpand={() => toggleExpanded(dKey)}
              meta={
                documentNames.length > 0 ? (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                    dExpanded ? 'bg-[#0077b5] text-white' : 'bg-[#919191]/15 text-[#919191]'
                  }`}>
                    {documentNames.length}
                  </span>
                ) : profile.documentMode === 'automatic' ? (
                  <span className="inline-flex items-center px-1.5 h-[18px] rounded-full text-[10px] font-semibold bg-[#F47F2E]/15 text-[#F47F2E]">
                    auto
                  </span>
                ) : null
              }
            >
              <span className="text-xs block">
                {/* `automatic` mode lets the crosswalk pick the evidence itself, so an empty
                    document list there means "all of it", not "none". */}
                Documents
                {profile.includeUnboundDocuments && (
                  <span className="ml-1 text-[#919191]">+ other documents</span>
                )}
              </span>
              {documentNames.length === 0 && (
                <span className="text-[11px] block text-[#919191]">
                  {profile.documentMode === 'automatic' ? 'Chosen automatically' : 'None'}
                </span>
              )}
            </Row>
            {dExpanded && documentNames.length > 0 && (
              <div
                className="border-b last:border-b-0 border-[#919191]/20 bg-gradient-to-b from-[#0077b5]/[0.04] to-transparent py-2.5 pr-3"
                style={{ paddingLeft: `${12 + (depth + 2) * 16}px` }}
              >
                <div className="flex flex-wrap gap-1.5">
                  {documentNames.map((name, i) => (
                    <span
                      key={`${dKey}${KEY_SEP}${i}`}
                      title={name}
                      className="group inline-flex items-center gap-1.5 max-w-[240px] pl-2 pr-2.5 py-1 text-[11px] leading-none bg-white border border-[#0077b5]/25 text-[#53545a] rounded-full shadow-sm hover:border-[#0077b5]/50 hover:shadow transition-all"
                    >
                      <FiFileText className="text-[#0077b5] text-xs flex-shrink-0" />
                      <span className="truncate">{name}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-[#919191]">
          Source Profile
          {!creating && profiles.length > 0 && ` (${profiles.length})`}
          {!creating && selectedProfiles.length > 0 && (
            <span className="ml-2 text-[#0b79b8] font-semibold">
              • {selectedProfiles.length} selected
            </span>
          )}
          {creating && <span className="ml-2 text-[#0b79b8] font-semibold">• New</span>}
        </h3>
        <div className="flex items-center gap-2">
          {!creating && selectedProfiles.length > 0 && (
            <>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
              >
                Clear
              </button>
              <span className="text-[#919191]/40">|</span>
            </>
          )}
          {!creating ? (
            <button
              type="button"
              onClick={startCreating}
              disabled={!canCreate}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={canCreate ? 'Create a source profile' : 'Select an assessment first'}
            >
              <FiPlus className="text-xs" /> New Profile
            </button>
          ) : (
            <button
              type="button"
              onClick={cancelCreating}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#919191] hover:text-[#53545a] transition-colors"
            >
              <FiX className="text-xs" /> Cancel
            </button>
          )}
        </div>
      </div>

      {creating ? (
        // Create form — name plus the raw source tree, which is only shown here.
        <div className="border border-[#919191]/30 p-3 space-y-3">
          <div>
            <label className="text-xs font-medium text-[#919191] block mb-1.5" htmlFor="source-profile-name">
              Profile name
            </label>
            <input
              id="source-profile-name"
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="e.g. SOC 2 evidence scope"
              className="w-full px-3 py-2 text-sm bg-white border border-[#919191]/30 focus:outline-none focus:ring-1 focus:ring-[#0b79b8] text-slate-600"
            />
          </div>

          <SourceDataSelector
            isOpen={isOpen}
            targetAssessmentIds={targetAssessmentIds}
            onSelectionChange={setDraftSelection}
          />

          {saveError && <p className="text-xs text-red-600">{saveError}</p>}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={cancelCreating}
              className="px-4 py-2 text-xs font-medium text-[#919191] hover:text-[#53545a] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveProfile}
              disabled={!draftName.trim() || saving}
              className="px-4 py-2 bg-[#0077b5] text-white text-xs font-medium hover:bg-[#005a8c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-[#919191]/30">
          <div className="max-h-40 overflow-y-auto">
            {loading && (
              <div className="px-4 py-3 space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-8 bg-gray-200 animate-pulse" />
                ))}
              </div>
            )}

            {!loading && groups.map((group) => {
              if (flatten) return group.profiles.map(p => renderProfile(p, 0, group.id));
              const gKey = `t${KEY_SEP}${group.id}`;
              const gExpanded = !expanded.has(gKey); // targets start expanded
              return (
                <React.Fragment key={gKey}>
                  <Row
                    expandable
                    expanded={gExpanded}
                    onToggleExpand={() => toggleExpanded(gKey)}
                    icon={<FiList className="text-base flex-shrink-0 text-[#919191]/50" />}
                    meta={`${group.profiles.length} profiles`}
                  >
                    <span className="text-sm block truncate" title={group.label}>{group.label}</span>
                  </Row>
                  {gExpanded && group.profiles.map(p => renderProfile(p, 1, group.id))}
                </React.Fragment>
              );
            })}

            {!loading && !hasRows && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-[#919191]">
                  {!targetKey
                    ? 'Select an assessment to see its source profiles'
                    : error
                    ? 'Could not load source profiles'
                    : 'No saved source profiles — create one to scope the crosswalk'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceProfileSelector;
